import React, {useEffect, useState} from 'react';
import {useParams, useLocation} from 'react-router-dom';
import {gradeApi} from '../../api';
import '../../App.css';
import {useAuth} from '../../auth/AuthContext';
import {Loading} from '../../components/Loading';
import NewTDetails from '../../components/Teacher/TGrading/NewTDetails';
import FlexTDetails from '../../components/Teacher/TGrading/FlexTDetails';

function TDetailsPage() {
    let {assignment_id, submission_id, class_id, regrade_question_id} = useParams();
    const [submissionIds, setSubmissionIds] = useState(null);
    const [submissionUnseenIds, setSubmissionUnseenIds] = useState(null);
    const [assignmentFormat, setAssignmentFormat] = useState(null);
    const [submissionData, setSubmissionData] = useState(null);

    // Get sort parameters from URL query string
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const sortKey = queryParams.get('sortKey') || '';
    const sortDir = queryParams.get('sortDir') || 'asc';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const {user} = useAuth();

    useEffect(() => {
        // Clear error and reset state when component mounts or dependencies change
        setError(null);
        setLoading(true);

        if (regrade_question_id) {
            getRegrades();
        } else {
            getSubmissions();
            getUnseenSubmissions();
            // Assignment type is now determined from submission object, not separate API call
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignment_id, regrade_question_id]);

    const getSubmissions = async () => {
        try {
            setError(null); // Clear any previous errors

            // Add sort parameters to the API request
            const response = await gradeApi.get({
                path: `/api/v2/assignments/${assignment_id}/submissions/ids`,
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    },
                    params: {
                        sortKey: sortKey,
                        sortDir: sortDir
                    }
                }
            });

            // console.log("Submissions IDs response:", response.data);

            if (response.data && response.data.ids) {
                setSubmissionIds(response.data.ids);

                // Get the submission details to check its type
                if (response.data.ids.length > 0) {
                    // Use the current submission_id if available, otherwise use the first ID
                    const submissionToCheck = submission_id || response.data.ids[0];
                    // console.log('Will check submission type for:', submissionToCheck);
                    getSubmissionDetails(submissionToCheck);
                } else {
                    console.warn('No submission IDs found in response');
                    setAssignmentFormat('standard'); // Default to standard if no submissions found
                    // Make sure we clear submission data if there are no submissions
                    setSubmissionData(null);
                }
            } else {
                console.error('Invalid response format:', response.data);
                setError('Unable to load submissions. Invalid response format.');
                setAssignmentFormat('standard');
            }

            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch submissions:', error);
            setError('Failed to fetch submissions. Please try again.');
            setLoading(false);
            setAssignmentFormat('standard'); // Default to standard on error
        }
    };

    const getUnseenSubmissions = async () => {
        try {
            setError(null); // Clear any previous errors

            const response = await gradeApi.get({
                path: `/api/v2/assignments/${assignment_id}/submissions/ids`,
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    },
                    params: {
                        isUnseenOnly: true
                    }
                }
            });

            setSubmissionUnseenIds(response.data.ids);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch unseen submissions:', error);
            setError('Failed to fetch unseen submissions. Please try again.');
            setLoading(false);
            setAssignmentFormat('standard'); // Default to standard on error
        }
    };

    const getRegrades = async () => {
        try {
            setError(null); // Clear any previous errors

            const response = await gradeApi.get({
                path: `/api/regrade-requests/`,
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });
            setSubmissionIds(response.data.map(regrade => regrade.id));
            setLoading(false);
        } catch (error) {
            console.error('Failed regrade requests:', error);
            setError('Failed regrade requests. Please try again.');
            setLoading(false);
            setAssignmentFormat('standard'); // Default to standard on error
        }
    };

    // Get submission details to determine assignment type
    const getSubmissionDetails = async (submissionId) => {
        try {
            // console.log('Fetching submission details for ID:', submissionId);
            // Clear any previous errors
            setError(null);

            // Use the correct endpoint format from NewTDetails.js
            const response = await gradeApi.get({
                path: `/api/assignment/${assignment_id}/submission/${submissionId}/`,
                config: {
                    headers: {
                        Authorization: `Bearer ${user.session_token}`
                    }
                }
            });

            if (!response.data) {
                throw new Error('Empty response received');
            }

            // Clear error since we got a successful response
            setError(null);

            // Store the full submission data for reference
            setSubmissionData(response.data);

            // console.log("Submission data received:", response.data);

            // Check if this is a byob_pdf_problem_set assignment
            // The path to the assignment type may differ from the v2 API
            const type = response.data.assignment_type;

            // console.log('Assignment type found:', type);
            // console.log('Response data structure:', JSON.stringify(response.data, null, 2));

            if (type === 'byob_pdf_problem_set') {
                // console.log('✅ This is a byob_pdf_problem_set assignment - using NewTDetails');
                setAssignmentFormat('byob_pdf_problem_set');
            } else if (type === 'flexible_problem_set') {
                // console.log('✅ This is a flexible_problem_set assignment - using FlexTDetails');
                setAssignmentFormat('flexible_problem_set');
            } else {
                // console.log('⚠️ Not a byob_pdf_problem_set or flexible_problem_set assignment - using standard TDetails');
                setAssignmentFormat('standard');
            }
        } catch (error) {
            console.error('Failed to fetch submission details:', error);
            setError('Unable to load submission details. Please try again.');
            // Default to regular TDetails on error
            setAssignmentFormat('standard');
        }
    };

    // Primary effect to load assignment type when submission ID changes
    useEffect(() => {
        if (submission_id) {
            // Always check the submission details when submission_id changes
            getSubmissionDetails(submission_id);
        }
    }, [submission_id]);

    // Also update whenever assignment_id changes
    useEffect(() => {
        if (assignment_id && submissionIds && submissionIds.length > 0) {
            const submissionToCheck = submission_id || submissionIds[0];
            getSubmissionDetails(submissionToCheck);
        }
    }, [assignment_id, submissionIds, submission_id]);

    // Debug the current assignment format whenever it changes
    // useEffect(() => {
    //     if (assignmentFormat) {
    // console.log('🔄 Format changed:', assignmentFormat, 'will use',
    //     assignmentFormat === 'byob_pdf_problem_set' ? 'NewTDetails' :
    //     assignmentFormat === 'flexible_problem_set' ? 'FlexTDetails' : 'TDetails');
    // }
    // }, [assignmentFormat]);

    return (
        <div>
            {loading ? (
                <Loading/>
            ) : error ? (
                <div className="p-4 text-red-600">{error}</div>
            ) : (
                <div className="w-full">
                    {submissionData && submissionData.assignment && submissionData.assignment.type === 'flexible_problem_set' ? (
                        <FlexTDetails
                            class_id={class_id}
                            regrade_question_id={regrade_question_id}
                            submissionIds={submissionIds}
                            submissionUnseenIds={submissionUnseenIds}
                        />
                    ) : (
                        <NewTDetails
                            class_id={class_id}
                            regrade_question_id={regrade_question_id}
                            submissionIds={submissionIds}
                            submissionUnseenIds={submissionUnseenIds}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export default TDetailsPage;
